import type { Router } from 'vue-router';

export type QuickActionId = 'administer' | 'prepare' | 'receive' | 'print';

export interface QuickActionDefinition {
  id: QuickActionId;
  /** i18next translation key resolved by consumers via `t(labelKey)`. */
  labelKey: string;
  iconName: string;
  /** Render variant for `iconName`; defaults to 'fill' at the consumer template. */
  iconVariant?: 'fill' | 'stroke';
  iconBgClass: string;
}

export const QUICK_ACTIONS: QuickActionDefinition[] = [
  {
    id: 'administer',
    labelKey: 'patient.actions.feed',
    iconName: 'FeedAdministration',
    iconBgClass: 'bg-[#7C4DFF]',
  },
  {
    id: 'prepare',
    labelKey: 'feed.prepareFeed',
    iconName: 'Prepare',
    iconVariant: 'stroke',
    iconBgClass: 'bg-[#F4B400]',
  },
  {
    id: 'receive',
    labelKey: 'inventory.receiveMilk',
    iconName: 'Receive',
    iconVariant: 'stroke',
    iconBgClass: 'bg-[#10B981]',
  },
  {
    id: 'print',
    labelKey: 'dashboard.quickActionPrint',
    iconName: 'PrintLabel',
    iconBgClass: 'bg-[#3B82F6]',
  },
];

export interface QuickActionHandlers {
  /** Opens the "Print Labels" modal on the current page. */
  openPrintLabels: () => void;
  /** Opens the "Receive Mother Milk" modal on the current page. */
  openReceiveMilk: () => void;
  /** Opens the "Administer Feed" modal on the current page. */
  openAdministerFeed: () => void;
}

/** Executes the shared quick-action behavior used by floating and dashboard action UIs. */
export async function executeQuickAction(
  actionId: QuickActionId,
  router: Router,
  handlers: QuickActionHandlers,
): Promise<void> {
  if (actionId === 'administer') {
    handlers.openAdministerFeed();
    return;
  }

  if (actionId === 'prepare') {
    await router.push('/feed-processing/prepare');
    return;
  }

  if (actionId === 'receive') {
    handlers.openReceiveMilk();
    return;
  }

  if (actionId === 'print') {
    handlers.openPrintLabels();
    return;
  }

  // Exhaustiveness check: TypeScript will error here if a new QuickActionId is
  // added to the union but not handled above.
  const _exhaustive: never = actionId;
  throw new Error(`Unhandled quick action: ${_exhaustive}`);
}
